// PATH: src/components/questReview/QuestEditor.tsx
import * as React from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { QuestBody } from "../../api/questReview/questReview";
import { editableFields, pathKey, setAt } from "./questEditing";

interface Props {
  open: boolean;
  body: QuestBody;
  saving: boolean;
  /** Raison du dernier refus du serveur (422), affichée telle quelle. */
  error?: string | null;
  onCancel: () => void;
  onSubmit: (body: QuestBody) => void;
}

/**
 * Le formulaire de correction : les TEXTES de la quête et la clé d'un choix,
 * rien d'autre (la structure est figée, voir `questEditing`). La version
 * enregistrée remplace l'ancienne pour tous les élèves qui recevront cette
 * quête.
 */
export const QuestEditor: React.FC<Props> = ({ open, body, saving, error, onCancel, onSubmit }) => {
  const [draft, setDraft] = React.useState<QuestBody>(body);
  React.useEffect(() => setDraft(body), [body, open]);
  const fields = React.useMemo(() => editableFields(draft), [draft]);
  const changed = JSON.stringify(draft) !== JSON.stringify(body);

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Corriger la quête</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seuls les textes et la bonne réponse se corrigent ; le nombre d'options, l'ordre attendu et la forme
          restent ceux de la quête. La version corrigée sera servie à tous les élèves suivants.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={1.5}>
          {fields.map((f) =>
            f.kind === "key" ? (
              <FormControlLabel
                key={pathKey(f.path)}
                control={
                  <Checkbox
                    checked={Boolean(f.value)}
                    onChange={(e) => setDraft((d) => setAt(d, f.path, e.target.checked))}
                  />
                }
                label={f.label}
              />
            ) : (
              <TextField
                key={pathKey(f.path)}
                label={f.label}
                value={f.value as string}
                onChange={(e) => setDraft((d) => setAt(d, f.path, e.target.value))}
                multiline={f.multiline}
                minRows={f.multiline ? 2 : undefined}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { dir: "auto" } }}
              />
            ),
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={saving}>
          Annuler
        </Button>
        <Button variant="contained" onClick={() => onSubmit(draft)} disabled={saving || !changed}>
          Enregistrer la correction
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestEditor;
